import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { Button } from 'components/UIElements'
import { formatAndSanitizeHTML } from 'utils/misc'
import { DirectionContext } from 'contexts/DirectionProvider'
import SurveyQuestion from '../SurveyQuestion'
import QuestionTitleImage from '../QuestionTitleImage'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import {
  checkLogicAndComplete,
  getChoiceAnswerValue,
  getShouldRenderNextQuestion,
  resolveChoiceIdFromStoredAnswer,
} from '../../utils/instrumentUtils'
import { RESPONSE_STATES } from 'utils/constants'

// Developer toggle for the UI shape.
// Set to `true` to render square-shaped "radio" buttons for horizontal select-one.
// Default is circle (`false`).
const RADIO_BUTTONS_SQUARE_FLAG = true

const toBooleanFlag = (value) => {
  // Supports boolean or common string/number representations from survey config.
  return value === true || value === 'true' || value === 1 || value === '1'
}

const normalizeChoiceLabelText = (rawLabel) => {
  return String(rawLabel ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const getChoiceDisplayData = (choice, choiceId) => {
  const candidates = [
    choice?.label,
    choice?.value,
    choice?.text,
    choice?.title,
    choiceId,
  ]

  for (const candidate of candidates) {
    const normalizedLabel = normalizeChoiceLabelText(candidate)
    if (normalizedLabel) {
      return {
        html: String(candidate),
        text: normalizedLabel,
      }
    }
  }

  return { html: '', text: '' }
}

const shouldRenderTextChoice = (choiceDisplayText) => {
  return choiceDisplayText.length >= 2 && /[a-z]/i.test(choiceDisplayText)
}

const hasRenderableContent = (rawValue) => {
  return normalizeChoiceLabelText(rawValue).length > 0
}

// GOLD-12297: in RTL the numbered buttons reverse, so swap left/right
// anchor text to keep each label next to the same value (paper translations).
const getHorizontalSelectOneAnchorLayout = ({
  leftAnchor = '',
  rightAnchor = '',
  isRTL = false,
} = {}) => {
  const displayedLeftAnchor = isRTL ? rightAnchor : leftAnchor
  const displayedRightAnchor = isRTL ? leftAnchor : rightAnchor
  return {
    displayedLeftAnchor,
    displayedRightAnchor,
    hasLeftAnchor: hasRenderableContent(displayedLeftAnchor),
    hasRightAnchor: hasRenderableContent(displayedRightAnchor),
  }
}

class HorizontalSelectOne extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      selected: null,
      selectingChoice: null,
      deselectingChoice: null,
      completed: false,
    }
  }

  componentDidMount() {
    const { storedAnswer, suppressAutoAdvance } = this.props

    const restoredChoiceId = resolveChoiceIdFromStoredAnswer(
      this.props.question?.choices,
      storedAnswer?.[0],
    )
    if (restoredChoiceId) {
      this.onSelectChoice(restoredChoiceId)
      if (!suppressAutoAdvance) {
        this.onCompleteQuestion()
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { selected } = this.state
    if (selected !== prevState.selected) {
      this.setState({ deselectingChoice: prevState.selected })
    }
  }

  onSetCompletion = () => {
    const { selected } = this.state
    this.setState({ completed: !!selected })
  }

  handleAnimationEnd = (e) => {
    if (e.animationName === 'selecting') {
      const { selectingChoice, deselectingChoice } = this.state
      if (selectingChoice) {
        this.setState({ selectingChoice: null })
      }
      if (deselectingChoice) {
        this.setState({ deselectingChoice: null })
      }
    }
  }

  onSelectChoice = (choiceId) => {
    const { setAllRequiredAnswered, id } = this.props
    this.setState(
      {
        selected: choiceId,
        selectingChoice: choiceId,
      },
      () => {
        setAllRequiredAnswered(false, id)
        this.onSetCompletion()
        try {
          const { question, webSurvey, completeQuestion } = this.props
          const { label, type, attributes } = question
          const { required } = attributes
          const selectedChoice = question.choices[choiceId]
          const choiceValue = getChoiceAnswerValue(question.choices, choiceId)
          const answer = [
            {
              label: selectedChoice?.label || selectedChoice?.value || '',
              choice_value: choiceValue,
              value: choiceValue,
              question: id,
              choice_id: choiceId,
            },
          ]
          const submission = {
            id,
            label,
            name: id,
            type,
            required,
            state: RESPONSE_STATES.answered,
            answer,
          }
          if (typeof completeQuestion === 'function' && webSurvey) {
            completeQuestion(submission, webSurvey.order?.[0])
          }
        } catch (e) {
          console.warn('HorizontalSelectOne autosave failed', { error: e })
        }
      },
    )
  }

  createAnswerArr = () => {
    const { question, id } = this.props
    const { choices } = question
    const { selected } = this.state
    if (selected) {
      const selectedChoice = choices[selected]
      const choiceValue = getChoiceAnswerValue(choices, selected)
      return [
        {
          label: selectedChoice?.label || selectedChoice?.value || '',
          choice_value: choiceValue,
          value: choiceValue,
          question: id,
          choice_id: selected,
        },
      ]
    }
    return []
  }

  onCompleteQuestion = () => {
    const {
      question,
      id,
      completeQuestion,
      nextQ,
      webSurvey,
      submissionSurvey,
      nextQId,
      reviewFunctionalitySupported,
      isConsent = false,
      isLastQuestion = false,
      isSibling = false,
    } = this.props
    const { label, type, attributes } = question
    const { required } = attributes
    const answer = this.createAnswerArr()
    const submission = {
      id,
      label,
      name: id,
      type,
      required,
      state: RESPONSE_STATES.answered,
      answer,
    }
    const currentAnswer = {
      [id]: answer,
    }

    const shouldRenderNextQ = getShouldRenderNextQuestion({
      isConsent,
      isLastQuestion,
      isSibling,
    })

    checkLogicAndComplete(
      webSurvey,
      id,
      submissionSurvey,
      currentAnswer,
      nextQId,
      nextQ,
      completeQuestion,
      submission,
      reviewFunctionalitySupported,
      shouldRenderNextQ,
    )
  }

  renderChoices = () => {
    const {
      question,
      id,
      renderBackButton,
      strings,
      submissionSurvey,
      webSurvey,
      isLastQuestion,
      isConsent,
      submissionLoading,
      isSibling = false,
      isRTL = false,
    } = this.props
    const { choices, choices_order, attributes } = question
    const {
      required,
      disable_buttons: disableButtons,
      // When enabled, render the "radio" (choice) buttons as squares instead of circles.
      radio_buttons_square: radioButtonsSquare,
      square_radio_buttons: squareRadioButtons,
      radio_button_shape: radioButtonShape,
    } = attributes || {}
    const {
      displayedLeftAnchor,
      displayedRightAnchor,
      hasLeftAnchor,
      hasRightAnchor,
    } = getHorizontalSelectOneAnchorLayout({
      leftAnchor: attributes?.left_anchor || '',
      rightAnchor: attributes?.right_anchor || '',
      isRTL,
    })
    const layoutClassName = `horizontal-select-one-layout${!hasLeftAnchor ? ' no-left-anchor' : ''}${!hasRightAnchor ? ' no-right-anchor' : ''}${!hasLeftAnchor && !hasRightAnchor ? ' no-anchors' : ''}`

    const normalizedRadioButtonShape =
      typeof radioButtonShape === 'string' ? radioButtonShape.toLowerCase() : ''
    const isSquareRadioButtons =
      RADIO_BUTTONS_SQUARE_FLAG ||
      toBooleanFlag(radioButtonsSquare) ||
      toBooleanFlag(squareRadioButtons) ||
      normalizedRadioButtonShape === 'square'
    const { selected, selectingChoice, deselectingChoice, completed } =
      this.state

    return (
      <div className='question-choices horizontal-select-one-choices'>
        <MakeScrollable>
          <div className={layoutClassName}>
            <div
              className={`horizontal-select-one-anchor left${!hasLeftAnchor ? ' empty' : ''}`}
              dangerouslySetInnerHTML={{
                __html: formatAndSanitizeHTML(displayedLeftAnchor),
              }}
            />
            <div className='horizontal-select-one-buttons'>
              {choices_order.map((choiceId) => {
                const choice = choices[choiceId]
                const choiceDisplay = getChoiceDisplayData(choice, choiceId)
                const renderAsTextChoice = shouldRenderTextChoice(
                  choiceDisplay.text,
                )
                return (
                  <Button
                    onClick={() => this.onSelectChoice(choiceId)}
                    onAnimationEnd={this.handleAnimationEnd}
                    key={`${choiceId}`}
                    className={`choice horizontal-choice${
                      selectingChoice === choiceId ? ' selecting' : ''
                    }${selected === choiceId ? ' selected' : ''}${
                      deselectingChoice === choiceId ? ' deselecting' : ''
                    }${isSquareRadioButtons ? ' square' : ''}${
                      renderAsTextChoice ? ' text-choice' : ''
                    }${disableButtons ? ' no-icon' : ''}`}>
                    {renderAsTextChoice ? (
                      <span
                        className='horizontal-choice-value'
                        dangerouslySetInnerHTML={{
                          __html: formatAndSanitizeHTML(
                            choiceDisplay.html || choiceDisplay.text,
                          ),
                        }}
                      />
                    ) : (
                      <span className='horizontal-choice-value'>
                        {choiceDisplay.text}
                      </span>
                    )}
                  </Button>
                )
              })}
            </div>
            <div
              className={`horizontal-select-one-anchor right${!hasRightAnchor ? ' empty' : ''}`}
              dangerouslySetInnerHTML={{
                __html: formatAndSanitizeHTML(displayedRightAnchor),
              }}
            />
          </div>
          {!isSibling && (
            <QuestionFooter
              completed={completed}
              id={id}
              isConsent={isConsent}
              isLastQuestion={isLastQuestion}
              loading={submissionLoading}
              onComplete={this.onCompleteQuestion}
              question={question}
              renderBackButton={renderBackButton}
              strings={strings}
              submissionSurvey={submissionSurvey}
              webSurvey={webSurvey}
            />
          )}
        </MakeScrollable>
      </div>
    )
  }

  render() {
    const { className, id, forwardedRef, question } = this.props
    const { label, imageSrc } = question || {}
    const _className = className ? ` ${className}` : ''

    return (
      <div ref={forwardedRef}>
        <SurveyQuestion id={id}>
          <MakeScrollable>
            <div
              className={`survey-question select one horizontal-select-one${_className}`}>
              {label && (
                <p className='question-label'>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: formatAndSanitizeHTML(label),
                    }}
                  />
                  <QuestionTitleImage imageSrc={imageSrc} questionId={id} />
                </p>
              )}
              {this.renderChoices()}
            </div>
          </MakeScrollable>
        </SurveyQuestion>
      </div>
    )
  }
}

const ForwardedHorizontalSelectOne = React.forwardRef((props, ref) => {
  const { isRTL } = useContext(DirectionContext)
  return <HorizontalSelectOne {...props} forwardedRef={ref} isRTL={isRTL} />
})

export default ForwardedHorizontalSelectOne

HorizontalSelectOne.propTypes = {
  className: PropTypes.string,
  completeQuestion: PropTypes.func,
  id: PropTypes.string,
  nextQ: PropTypes.func,
  nextQId: PropTypes.func,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  setAllRequiredAnswered: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  submissionSurvey: PropTypes.shape({
    questions: PropTypes.array,
  }),
  webSurvey: PropTypes.object,
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  isRTL: PropTypes.bool,
}
